/*
    Copyright (C) 2017 Red Hat, Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

            http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Component, Input } from '@angular/core';

import { ConfigModel } from '../../models/config.model';
import { Field } from '../../models/field.model';
import { FieldMappingPair } from '../../models/mapping.model';
import { DocumentDefinition } from '../../models/document-definition.model';

@Component({
  selector: 'collection-mapping',
  template: `<div class="collectionSectionContainer">
  <div [attr.class]="'collectionSection clearfix ' + getAnimationCSSClass()">
    <!-- collection field pairing detail -->
    <div style="width:50%; padding-top:10px;" class="pull-left mappingFieldContainer">
      <div *ngFor="let fieldPair of cfg.mappings.activeMapping.fieldMappings">
        <div class="MappingFieldSection">
          <!-- header label / trash icon -->
          <div class="clearfix">
            <div class="pull-left">
              <label>Source</label>
              <i class="fa fa-bolt" style="font-size:12px; vertical-align:baseline;" *ngIf="fieldPair.hasTransition()"></i>
            </div>
            <div class="pull-right text-right" style="margin-right:5px; font-size:15px">
              <i class="fa fa-edit link" aria-hidden="true" (click)="editPair(fieldPair)" tooltip="Edit mapping" placement="left"></i>
              <i class="fa fa-trash link" aria-hidden="true" (click)="removePair(fieldPair)" tooltip="Remove mapping" placement="left"></i>
            </div>
          </div>
          <mapping-field-detail *ngFor="let mappedField of fieldPair.getMappedFields(true)" [mappedField]="mappedField" [fieldPair]="fieldPair"
            [cfg]="cfg" [isSource]="true"></mapping-field-detail>
          <div class="pull-left">
            <label>Target</label>
          </div>
          <div class="clear"></div>
          <mapping-field-detail *ngFor="let mappedField of fieldPair.getMappedFields(false)" [mappedField]="mappedField" [fieldPair]="fieldPair"
            [cfg]="cfg" [isSource]="false"></mapping-field-detail>
        </div>
      </div>
      <!-- add button -->
      <div class="linkContainer">
        <button type="button" class="btn btn-primary" (click)="addClicked()">Add Mapping</button>
      </div>
    </div>
    <div class="pull-left" style="width:50%; margin:0; padding:0" *ngIf="fieldPairForEditing">
      <div class="card-pf-title clearfix">
        <div class="pull-left">Edit Details</div>
        <div class="pull-right">
          <i class="fa fa-close link" aria-hidden="true" (click)="exitEditMode()"></i>
        </div>
      </div>
      <mapping-pair-detail [cfg]="cfg" [fieldPair]="fieldPairForEditing" [modalWindow]="modalWindow"></mapping-pair-detail>
    </div>
  </div>
</div>`,
})

export class CollectionMappingComponent {
  @Input() cfg: ConfigModel;

  fieldPairForEditing: FieldMappingPair = null;
  private animateLeft = false;
  private animateRight = false;

  getAnimationCSSClass(): string {
    if (this.animateLeft) {
      return 'dm-swipe-left collectionSectionLeft';
    } else if (this.animateRight) {
      return 'dm-swipe-right';
    }
    return '';
  }

  getFields(fieldPair: FieldMappingPair, isSource: boolean): Field[] {
    const fields: Field[] = fieldPair.getFields(isSource);
    return (fields.length > 0) ? fields : [DocumentDefinition.getNoneField()];
  }

  addClicked(): void {
    this.cfg.mappingService.addMappedPair();
  }

  editPair(fieldPair: FieldMappingPair): void {
    this.fieldPairForEditing = fieldPair;
    this.cfg.mappings.activeMapping.currentFieldMapping = fieldPair;
    this.animateLeft = true;
  }

  exitEditMode(): void {
    this.fieldPairForEditing = null;
    this.animateLeft = false;
    this.animateRight = true;
    this.cfg.mappings.activeMapping.currentFieldMapping = null;
  }

  removePair(fieldPair: FieldMappingPair): void {
    this.cfg.mappingService.removeMappedPair(fieldPair);
  }
}
